import {
  InAppMessageViewEvent,
  InAppMessageImpressionViewEvent,
  InAppMessageActionViewEvent,
  InAppMessageCloseViewEvent,
  InAppMessageImageImpressionViewEvent
} from "./InAppMessageViewEvent"
import { InAppMessageView } from "../view/InAppMessageView"
import { InAppMessageActionHandlerFactory } from "./InAppMessageActionHandler"
import {
  InAppMessageActionConverter,
  InAppMessageConverter,
  InAppMessageViewConverter
} from "../../InAppMessageConverter"

export interface InAppMessageViewEventActor<ViewEvent extends InAppMessageViewEvent = InAppMessageViewEvent> {
  supports(viewEvent: InAppMessageViewEvent): boolean
  action(view: InAppMessageView, viewEvent: ViewEvent): void
}

export class InAppMessageViewEventActorFactory {
  constructor(private readonly actors: InAppMessageViewEventActor[]) {}

  get(event: InAppMessageViewEvent): InAppMessageViewEventActor<InAppMessageViewEvent> | undefined {
    return this.actors.find((actor) => actor.supports(event))
  }
}

export class InAppMessageImpressionEventActor implements InAppMessageViewEventActor<InAppMessageImpressionViewEvent> {
  supports(event: InAppMessageViewEvent): event is InAppMessageImpressionViewEvent {
    return event.type === "IMPRESSION"
  }

  action(view: InAppMessageView, event: InAppMessageImpressionViewEvent): void {
    // Do nothing.
  }
}

export class InAppMessageActionEventActor implements InAppMessageViewEventActor<InAppMessageActionViewEvent> {
  constructor(private readonly actionHandlerFactory: InAppMessageActionHandlerFactory) {}

  supports(event: InAppMessageViewEvent): event is InAppMessageActionViewEvent {
    return event.type === "ACTION"
  }

  action(view: InAppMessageView, event: InAppMessageActionViewEvent): void {
    const handler = this.actionHandlerFactory.get(event.action)
    const processed = view.ui.inAppMessageListener?.onInAppMessageClick?.(
      InAppMessageConverter.toPublic(view.context.inAppMessage),
      InAppMessageViewConverter.toPublic(view),
      InAppMessageActionConverter.toPublic(event.action)
    )
    if (processed || !view.status.canClose()) return

    handler?.handle(view, event.action)
  }
}

export class InAppMessageCloseEventActor implements InAppMessageViewEventActor<InAppMessageCloseViewEvent> {
  supports(event: InAppMessageViewEvent): event is InAppMessageCloseViewEvent {
    return event.type === "CLOSE"
  }

  action(view: InAppMessageView, event: InAppMessageCloseViewEvent): void {
    // Do nothing. This method is called after the view is closed.
  }
}

export class InAppMessageImageImpressionEventActor
  implements InAppMessageViewEventActor<InAppMessageImageImpressionViewEvent>
{
  supports(event: InAppMessageViewEvent): event is InAppMessageImageImpressionViewEvent {
    return event.type === "IMAGE_IMPRESSION"
  }

  action(view: InAppMessageView, event: InAppMessageImageImpressionViewEvent): void {
    // Do nothing.
  }
}
